// Calculates the AI Readiness score from the multi-step form results
// Input shape (abbreviated):
// {
//   visionLeadership: { executiveSponsorship: boolean, strategicAlignment: boolean, scenarioPlanning: boolean },
//   governanceEthics: { dnaBalancedGovernance: boolean, ethicsEquity: 1-5, responsibleAIPolicy: 1-5, complianceRiskAssessmentProcess: 1-5 },
//   dataInfrastructure: { dataReadiness: 1-5, shadowAIVisibility: boolean, aiSandbox: boolean, platformMandate: boolean },
//   talentCulture: { aiLiteracy: 1-5, innovationCulture: boolean, facultyStaffEngagement: 1-5 },
//   implementationContinuousImprovement: { useCasePrioritization: 1-5, pilotEvaluateScale: 1-5, ongoingMetricsRoi: 1-5 }
// }

export const MIN_SCORE = 14;
export const MAX_SCORE = 155;

function toBoolean(value) {
  return value === true || value === 'Y' || value === 'y' || value === 'Yes' || value === 'yes';
}

function toRating(value, defaultValue = 1) {
  const num = Number(value);
  if (Number.isFinite(num)) {
    return Math.min(5, Math.max(1, Math.round(num)));
  }
  return defaultValue;
}

function scoreBoolean(value, yesPoints) {
  return toBoolean(value) ? yesPoints : 0;
}

/**
 * Calculate total AI readiness score and a per-question breakdown
 * @param {object} results - JSON object produced by the form
 * @returns {{total:number, min:number, max:number, breakdown:Record<string, number>, categoryBreakdown:Record<string, number>}}
 */
export function calculateScore(results) {
  const r = results || {};
  const v = r.visionLeadership || {};
  const g = r.governanceEthics || {};
  const d = r.dataInfrastructure || {};
  const t = r.talentCulture || {};
  const i = r.implementationContinuousImprovement || {};

  const breakdown = {
    executiveSponsorship: scoreBoolean(v.executiveSponsorship, 10),
    strategicAlignment: scoreBoolean(v.strategicAlignment, 5),
    scenarioPlanning: scoreBoolean(v.scenarioPlanning, 5),

    dnaBalancedGovernance: scoreBoolean(g.dnaBalancedGovernance, 15),
    ethicsEquity: toRating(g.ethicsEquity),
    responsibleAIPolicy: toRating(g.responsibleAIPolicy),
    complianceRiskAssessmentProcess: toRating(g.complianceRiskAssessmentProcess),

    dataReadiness: toRating(d.dataReadiness) * 5,
    shadowAIVisibility: scoreBoolean(d.shadowAIVisibility, 15),
    aiSandbox: scoreBoolean(d.aiSandbox, 10),
    platformMandate: scoreBoolean(d.platformMandate, 15),

    aiLiteracy: toRating(t.aiLiteracy),
    innovationCulture: scoreBoolean(t.innovationCulture, 10),
    facultyStaffEngagement: toRating(t.facultyStaffEngagement),

    useCasePrioritization: toRating(i.useCasePrioritization) * 2,
    pilotEvaluateScale: toRating(i.pilotEvaluateScale),
    ongoingMetricsRoi: toRating(i.ongoingMetricsRoi)
  };

  const categoryBreakdown = {
    visionLeadership:{score:
      breakdown.executiveSponsorship +
      breakdown.strategicAlignment +
      breakdown.scenarioPlanning,
      min: 0,
      max: 20
    },
    governanceEthics:{score:
      breakdown.dnaBalancedGovernance +
      breakdown.ethicsEquity +
      breakdown.responsibleAIPolicy +
      breakdown.complianceRiskAssessmentProcess,
      min: 3,
      max: 30
    },
    dataInfrastructure:{score:
      breakdown.dataReadiness +
      breakdown.shadowAIVisibility +
      breakdown.aiSandbox +
      breakdown.platformMandate,
      min: 5,
      max: 65
    },
    talentCulture:{score:
      breakdown.aiLiteracy +
      breakdown.innovationCulture +
      breakdown.facultyStaffEngagement,
      min: 2,
      max: 20
    },
    implementationContinuousImprovement:{score:
      breakdown.useCasePrioritization +
      breakdown.pilotEvaluateScale +
        breakdown.ongoingMetricsRoi,
      min: 4,
      max: 20,
    }
  };
  categoryBreakdown.implementationContinuousImprovement.score100 = mapScoreTo100(categoryBreakdown.implementationContinuousImprovement.score, categoryBreakdown.implementationContinuousImprovement.min, categoryBreakdown.implementationContinuousImprovement.max);
  categoryBreakdown.dataInfrastructure.score100 = mapScoreTo100(categoryBreakdown.dataInfrastructure.score, categoryBreakdown.dataInfrastructure.min, categoryBreakdown.dataInfrastructure.max);
  categoryBreakdown.talentCulture.score100 = mapScoreTo100(categoryBreakdown.talentCulture.score, categoryBreakdown.talentCulture.min, categoryBreakdown.talentCulture.max);
  categoryBreakdown.governanceEthics.score100 = mapScoreTo100(categoryBreakdown.governanceEthics.score, categoryBreakdown.governanceEthics.min, categoryBreakdown.governanceEthics.max);
  categoryBreakdown.visionLeadership.score100 = mapScoreTo100(categoryBreakdown.visionLeadership.score, categoryBreakdown.visionLeadership.min, categoryBreakdown.visionLeadership.max);

  const total = Object.values(categoryBreakdown).reduce((sum, v) => sum + v.score, 0);

  return { total, min: MIN_SCORE, max: MAX_SCORE, breakdown, categoryBreakdown, score100: mapScoreTo100(total, MIN_SCORE, MAX_SCORE) };
}
/**
 * Maps a raw score to a 1-100 scale based on min and max possible scores.
 * @param {number} score - The raw score to map.
 * @param {number} min - The minimum possible score.
 * @param {number} max - The maximum possible score.
 * @returns {number} The mapped score on a 1-100 scale (rounded to nearest integer).
 */
function mapScoreTo100(score, min, max) {
  if (max === min) return 100;
  const normalized = (score - min) / (max - min);
  return Math.round(normalized * 100);
}

export default calculateScore;


